/**
 * Frontend client for the Insights API (server/modules/insights). Authed reads
 * go through the shared workspace-scoped fetch; `track` is a public, no-auth,
 * fire-and-forget download beacon.
 */
import { apiGet } from '../shared/apiClient';

export interface InsightsSummary {
  linkViews: number;
  activeClients: number;
  avgTimeToApprovalDays: number;
  firstPassApprovalPct: number;
  linkViewsDelta: number;
  activeClientsDelta: number;
  avgTimeToApprovalDaysDelta: number;
  firstPassApprovalPctDelta: number;
}

export interface HotDesign {
  fileId: string;
  name: string;
  views: number;
  hot: boolean;
}

export interface ClientEngagement {
  clientName: string;
  views: number;
  downloads: number;
  lastSeen: string;
  signal: 'hot' | 'warm' | 'cold';
}

export interface ActivityItem {
  type: 'link_view' | 'download' | 'approve' | 'comment';
  clientName: string | null;
  fileId: string | null;
  fileName?: string;
  ts: string;
}

export const getInsightsSummary = (days = 30) =>
  apiGet<InsightsSummary>(`/insights/summary?days=${days}`);

export const getHottestDesigns = (days = 30) =>
  apiGet<HotDesign[]>(`/insights/hottest?days=${days}`);

export const getClientEngagement = (days = 30) =>
  apiGet<ClientEngagement[]>(`/insights/clients?days=${days}`);

/** Recent workspace activity, newest first (limit 30). `fileId` scopes to one file. */
export const getActivity = (fileId?: string) =>
  apiGet<ActivityItem[]>(`/insights/activity${fileId ? `?fileId=${fileId}` : ''}`);

/** Public download beacon — no auth, fire-and-forget (errors swallowed). */
export function track(token: string, type: 'download', fileId?: string, meta?: Record<string, unknown>): void {
  void fetch('/api/v1/insights/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, type, ...(fileId ? { fileId } : {}), ...(meta ? { meta } : {}) }),
  }).catch(() => {});
}
